import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Star, 
  MapPin, 
  DollarSign,
  CheckCircle2,
  ArrowLeft,
  User,
  Calendar,
  Image as ImageIcon,
  MessageSquare,
  Send,
  ChefHat,
  Wine,
  Music,
  Users,
  Truck,
  Package,
  Clock,
  ListChecks,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER_IMAGES = {
  chef: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400',
  bartender: 'https://images.unsplash.com/photo-1516997121675-4c2d1684aa3e?w=400',
  dj: 'https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?w=400',
  photographer: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?w=400',
  server: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=400',
  coordinator: 'https://images.unsplash.com/photo-1560439514-4e9645039924?w=400',
  setup: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400'
};

const CATEGORY_ICONS = {
  food: <ChefHat className="h-5 w-5" />,
  drinks: <Wine className="h-5 w-5" />,
  entertainment: <Music className="h-5 w-5" />,
  staffing: <Users className="h-5 w-5" />,
  logistics: <Truck className="h-5 w-5" />
};

export default function VendorProfile() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '', service_id: '', service_name: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', content: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [expandedService, setExpandedService] = useState(null);

  useEffect(() => {
    fetchVendor();
  }, [vendorId]);

  const fetchVendor = async () => {
    try {
      const [vendorsRes, reviewsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/vendors/${vendorId}/reviews`),
        axios.get(`${API}/vendors/${vendorId}/services`)
      ]);
      
      const foundVendor = vendorsRes.data.find(v => v.vendor_id === vendorId);
      if (!foundVendor) {
        toast.error('Vendor not found');
        navigate('/marketplace');
        return;
      }
      
      setVendor(foundVendor);
      setReviews(reviewsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching vendor:', error);
      toast.error('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to leave a review');
      return;
    }
    if (reviewForm.rating < 1 || reviewForm.rating > 5) {
      toast.error('Please select a rating');
      return;
    }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/vendors/${vendorId}/reviews`, {
        rating: reviewForm.rating,
        comment: reviewForm.comment,
        service_id: reviewForm.service_id || undefined,
        service_name: reviewForm.service_name || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review submitted!');
      setReviewDialogOpen(false);
      setReviewForm({ rating: 5, comment: '', service_id: '', service_name: '' });
      fetchVendor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleSendMessage = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to message vendors');
      return;
    }
    if (!messageForm.content.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: vendor.user_id,
        subject: messageForm.subject || `Message from ${user?.name || 'a customer'}`,
        content: messageForm.content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent to vendor!');
      setShowMessageDialog(false);
      setMessageForm({ subject: '', content: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getProfileImage = () => {
    if (vendor?.profile_image) return `${API}/files/${vendor.profile_image}`;
    return PLACEHOLDER_IMAGES[vendor?.category] || PLACEHOLDER_IMAGES.chef;
  };

  const getServiceImages = () => {
    const images = [];
    if (vendor?.portfolio_images?.length > 0) {
      vendor.portfolio_images.forEach(img => images.push(`${API}/files/${img}`));
    }
    services.forEach(svc => {
      if (svc.image_url) images.push(svc.image_url);
    });
    return images;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading vendor...</div>
      </div>
    );
  }

  if (!vendor) return null;

  const serviceImages = getServiceImages();

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/marketplace')}
          className="mb-6 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Marketplace
        </Button>

        {/* Profile Header */}
        <Card className="bg-card border-border mb-8 overflow-hidden">
          <div className="relative h-40 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          <CardContent className="relative px-6 pb-6">
            <div className="flex flex-col md:flex-row md:items-end -mt-16 gap-6">
              <div className="w-32 h-32 rounded-xl border-4 border-border overflow-hidden bg-muted flex-shrink-0">
                <img src={getProfileImage()} alt={vendor.business_name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <h1 className="font-heading text-2xl font-bold text-foreground">{vendor.business_name}</h1>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 capitalize">
                        {vendor.category}
                      </Badge>
                      {vendor.is_verified && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Verified
                        </Badge>
                      )}
                      <div className="flex items-center text-yellow-500">
                        <Star className="h-4 w-4 fill-yellow-500" />
                        <span className="ml-1 text-sm font-medium">{vendor.rating?.toFixed(1) || 'N/A'}</span>
                        <span className="ml-1 text-xs text-muted-foreground">({vendor.reviews_count || 0} reviews)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <div className="flex items-center"><DollarSign className="h-4 w-4 mr-1" />${vendor.hourly_rate}/hr</div>
                      {vendor.service_areas?.length > 0 && (
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          {vendor.service_areas.slice(0, 3).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => isAuthenticated ? setShowMessageDialog(true) : toast.error('Please sign in to message')}
                      variant="outline"
                      className="border-border text-foreground hover:bg-muted"
                      data-testid="message-vendor-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Message
                    </Button>
                    <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="write-review-btn">
                          <Star className="h-4 w-4 mr-2" /> Write Review
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-border">
                        <DialogHeader>
                          <DialogTitle className="text-foreground">Write a Review</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {services.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-muted-foreground">Service (optional)</Label>
                              <select
                                value={reviewForm.service_id}
                                onChange={(e) => {
                                  const svc = services.find(s => s.item_id === e.target.value);
                                  setReviewForm({...reviewForm, service_id: e.target.value, service_name: svc?.name || ''});
                                }}
                                className="w-full h-10 rounded-md border border-border bg-muted text-foreground px-3 text-sm"
                                data-testid="review-service-select"
                              >
                                <option value="">General review</option>
                                {services.map(s => (
                                  <option key={s.item_id} value={s.item_id}>{s.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">Rating</Label>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map(star => (
                                <button key={star} type="button" onClick={() => setReviewForm({...reviewForm, rating: star})} className="p-1 hover:scale-110 transition-transform">
                                  <Star className={`h-8 w-8 ${star <= reviewForm.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-muted-foreground">Your Review</Label>
                            <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})} placeholder="Share your experience..." className="bg-muted border-border text-foreground min-h-[100px]" />
                          </div>
                          <Button onClick={handleSubmitReview} className="w-full bg-indigo-500 hover:bg-indigo-600" disabled={submittingReview}>
                            {submittingReview ? 'Submitting...' : 'Submit Review'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {vendor.description && <p className="text-muted-foreground mt-4">{vendor.description}</p>}
                {vendor.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {vendor.skills.map((skill, i) => (
                      <Badge key={i} variant="outline" className="border-border text-muted-foreground">{skill}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Images Gallery */}
        {serviceImages.length > 0 && (
          <Card className="bg-card border-border mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-foreground flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-indigo-400" />
                Service Gallery
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {serviceImages.map((img, idx) => (
                  <button key={idx} onClick={() => setSelectedImage(img)} className="aspect-square rounded-lg overflow-hidden bg-muted hover:ring-2 ring-indigo-500 transition-all">
                    <img src={img} alt={`Service ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Service Listings - Main Content */}
          <div className="lg:col-span-2">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-foreground flex items-center gap-2">
                  <Package className="h-5 w-5 text-indigo-400" />
                  Available Services ({services.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {services.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No services listed yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {services.map((svc) => (
                      <div key={svc.item_id} className="border border-border rounded-xl overflow-hidden hover:border-indigo-500/30 transition-all">
                        <div className="flex flex-col sm:flex-row">
                          {svc.image_url && (
                            <div className="sm:w-40 h-32 sm:h-auto flex-shrink-0">
                              <img src={svc.image_url} alt={svc.name} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1 p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-foreground text-lg flex items-center gap-2">
                                  {CATEGORY_ICONS[svc.category]}
                                  {svc.name}
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">{svc.description}</p>
                              </div>
                              <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 ml-3 whitespace-nowrap">
                                ${svc.price?.toFixed(2)}
                              </Badge>
                            </div>
                            
                            {/* Expandable Details */}
                            <button 
                              onClick={() => setExpandedService(expandedService === svc.item_id ? null : svc.item_id)}
                              className="text-indigo-400 text-sm hover:text-indigo-300 transition-colors mt-2"
                              data-testid={`expand-service-${svc.item_id}`}
                            >
                              {expandedService === svc.item_id ? 'Hide Details' : 'View Details'}
                            </button>
                            
                            {expandedService === svc.item_id && (
                              <div className="mt-4 space-y-4 border-t border-border pt-4">
                                {svc.what_to_expect && (
                                  <div>
                                    <h4 className="text-sm font-medium text-indigo-400 flex items-center gap-1 mb-2">
                                      <Clock className="h-4 w-4" /> What to Expect
                                    </h4>
                                    <p className="text-sm text-muted-foreground">{svc.what_to_expect}</p>
                                  </div>
                                )}
                                {svc.whats_included?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-indigo-400 flex items-center gap-1 mb-2">
                                      <ListChecks className="h-4 w-4" /> What's Included
                                    </h4>
                                    <ul className="grid grid-cols-2 gap-1">
                                      {svc.whats_included.map((item, i) => (
                                        <li key={i} className="text-sm text-muted-foreground flex items-center gap-1">
                                          <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Reviews */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-foreground">Reviews ({reviews.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-8"><p className="text-muted-foreground">No reviews yet</p></div>
                ) : (
                  <div className="space-y-4">
                    {reviews.slice(0, 5).map((review, idx) => (
                      <div key={idx} className="border-b border-border pb-4 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                              <User className="h-4 w-4 text-indigo-400" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-foreground">{review.reviewer_name}</span>
                              {review.service_name && (
                                <p className="text-[10px] text-muted-foreground">{review.service_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`h-3 w-3 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} />
                            ))}
                          </div>
                        </div>
                        {review.comment && <p className="text-sm text-muted-foreground">{review.comment}</p>}
                        <p className="text-xs text-muted-foreground mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Availability */}
            {vendor.availability?.available_dates?.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-foreground text-lg">Availability</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {vendor.availability.available_dates.slice(0, 6).map((date, idx) => (
                      <Badge key={idx} variant="outline" className="border-green-500/30 text-green-400">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </Badge>
                    ))}
                    {vendor.availability.available_dates.length > 6 && (
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        +{vendor.availability.available_dates.length - 6} more
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Contact */}
            <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-500/30">
              <CardContent className="pt-6 text-center">
                <MessageSquare className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Have Questions?</h3>
                <p className="text-sm text-muted-foreground mb-4">Message {vendor.business_name} directly through the platform</p>
                <Button 
                  onClick={() => isAuthenticated ? setShowMessageDialog(true) : toast.error('Please sign in')}
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                  data-testid="sidebar-message-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Image Lightbox */}
        {selectedImage && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
            <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-foreground"><X className="h-8 w-8" /></button>
            <img src={selectedImage} alt="Gallery" className="max-w-full max-h-full rounded-lg" />
          </div>
        )}

        {/* Message Dialog */}
        <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-400" />
                Message {vendor.business_name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Send a direct message to this vendor
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Subject</Label>
                <Input 
                  value={messageForm.subject} 
                  onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                  placeholder="e.g., Inquiry about catering for June wedding"
                  className="bg-muted border-border text-foreground"
                  data-testid="message-subject"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Message</Label>
                <Textarea 
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({...messageForm, content: e.target.value})}
                  placeholder="Tell the vendor about your event, needs, and questions..."
                  className="bg-muted border-border text-foreground min-h-[120px]"
                  data-testid="message-content"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMessageDialog(false)} className="border-border text-muted-foreground">
                Cancel
              </Button>
              <Button onClick={handleSendMessage} disabled={sendingMessage} className="bg-indigo-500 hover:bg-indigo-600" data-testid="send-message-btn">
                <Send className="h-4 w-4 mr-2" />
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
